import type { StoreControl } from 'commons';
import type { SeriesOption } from 'echarts';
import { v4 } from 'uuid';
import { SERIES_STYLES_MAP } from '../statics';

/**
 * Represents a chart series with configurable styling and display options
 */
export class DtisSeries {
  /**
   * The symbol to use for data points
   */
  symbol: string | undefined;

  /**
   * The name of the series
   */
  name: string | undefined;

  /**
   * Line marking configuration
   */
  markLine?: {
    data: unknown;
  };

  /**
   * Unique identifier for the series
   */
  readonly id: string = v4();

  /**
   * Series data points
   */
  data: unknown = [0, 0, 0];

  /**
   * Line style configuration
   */
  lineStyle?: {
    color?: string;
    opacity?: string;
    join?: string;
    [key: string]: unknown;
  } = {};

  /**
   * Item style configuration
   */
  itemStyle?: {
    color: string;
    [key: string]: unknown;
  };

  /**
   * Area style configuration
   */
  areaStyle: {
    opacity: number;
    [key: string]: unknown;
  } = {
    opacity: 0,
  };

  /**
   * Whether to show all symbols
   */
  showAllSymbol: string | boolean = 'auto';

  /**
   * Whether to connect null values
   */
  connectNulls = false;

  /**
   * Size of the symbol
   */
  symbolSize = 4;

  /**
   * Emphasis configuration for highlighting
   */
  emphasis = {
    itemStyle: {
      borderWidth: 1,
      borderColor: '',
    },
  };

  /**
   * Creates a new series instance
   * @param data - Series data points
   * @param options - Additional configuration options
   */
  constructor(data?: unknown, options?: Partial<SeriesOption>) {
    // biome-ignore lint/complexity/useLiteralKeys: echarts
    Object.assign(this, SERIES_STYLES_MAP['line']);

    if (options) {
      Object.assign(this, options);
    }

    if (data) {
      this.data = data;
    }
  }

  /**
   * Sets a mark line on the series
   * @param index - The index to check against
   * @param markLine - Mark line data
   */
  setMarkline(index: number, markLine: unknown): void {
    this.markLine = {
      data: index === 0 ? markLine : undefined,
    };
  }
  /**
   * Apply a color to the series style
   * @param color - The color to apply
   */
  applyColor(color: string): void {
    this.lineStyle = {
      ...this.lineStyle,
      color: color ?? this.lineStyle?.color,
    };
    this.itemStyle = {
      ...this.itemStyle,
      color: color ?? this.itemStyle?.color,
    };

    this.emphasis.itemStyle.borderColor = color ?? this.emphasis.itemStyle.borderColor;
  }

  /**
   * Apply control settings to the series
   * @param control - The store control with configuration
   */
  applyControl(control: StoreControl): void {
    if (control.symbol !== undefined) {
      this.symbol = control.symbol === 'none' ? 'circle' : control.symbol;
    }

    this.name = control.user_label === '' ? this.name : control.user_label;
  }
}
